import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Mountain, Save, X, Upload } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import MapCoordinatePicker from '../components/MapCoordinatePicker';

interface POI {
  id: string;
  name: string;
  description: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  type: string;
  connectedPOIs?: string[]; // IDs di altri POI collegati
}

interface Trail {
  id: string;
  name: string;
  description: string;
  distance: string;
  duration: string;
  difficulty: string;
  elevation: string;
  distanceFromCasalVelino: string;
  image: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  externalLink?: string;
  category: string;
  highlights: string[];
  tips: string;
  pois?: POI[];
}

const TrailsNew = () => {
  const [trails, setTrails] = useState<Trail[]>([]);
  const [selectedTrail, setSelectedTrail] = useState<Trail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Omit<Trail, 'id'>>({
    name: '',
    description: '',
    distance: '',
    duration: '',
    difficulty: '',
    elevation: '',
    distanceFromCasalVelino: '',
    image: '',
    coordinates: {
      latitude: 0,
      longitude: 0
    },
    externalLink: '',
    category: '',
    highlights: [],
    tips: '',
    pois: []
  });

  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null);
  const [isEditingPOI, setIsEditingPOI] = useState(false);
  const [poiFormData, setPoiFormData] = useState<POI>({
    id: '',
    name: '',
    description: '',
    coordinates: { latitude: 0, longitude: 0 },
    type: 'punto_interesse',
    connectedPOIs: []
  });

  // Dati hardcoded dall'app mobile (da sincronizzare)
  const mobileTrails: Trail[] = [
    {
      id: '1',
      name: 'Sentiero Blu (Casal Velino – Acciaroli)',
      description: 'Uno dei grandi classici del Cilento, un cammino che unisce mare e collina lungo le pendici meridionali del Monte Stella. Antica via di collegamento tra i borghi costieri e quelli interni.',
      distance: '16,98 km',
      duration: '7 ore',
      difficulty: 'Media',
      elevation: '470 m',
      distanceFromCasalVelino: '0 km',
      image: '/placeholder-trail.jpg',
      coordinates: {
        latitude: 40.1689,
        longitude: 15.1428
      },
      category: 'Escursionistico',
      highlights: ['Panorami marini', 'Macchia mediterranea', 'Borghi storici'],
      tips: 'Portare acqua e cibo. Percorso impegnativo ma molto panoramico.'
    },
    {
      id: '2',
      name: 'Sentiero Pioppi – Pollica – Pozzo dell\'Uva Nera – Celso – Casal Velino',
      description: 'Percorso che parte dal borgo marinaro di Pioppi e segue un antico sentiero naturalistico recuperato da Legambiente. Immerso nella macchia mediterranea, costeggia il torrente Mortella.',
      distance: '3 km',
      duration: '2 ore e mezza',
      difficulty: 'Facile',
      elevation: '100 m',
      distanceFromCasalVelino: '6 km',
      image: '/placeholder-trail.jpg',
      coordinates: {
        latitude: 40.1750,
        longitude: 15.0800
      },
      category: 'Naturalistico',
      highlights: ['Torrente Mortella', 'Macchia mediterranea', 'Borgo di Pioppi'],
      tips: 'Percorso adatto a tutti. Ideale per famiglie.'
    },
    {
      id: '3',
      name: 'Sentiero degli Eremiti – San Giovanni a Piro',
      description: 'Un percorso che attraversa paesaggi suggestivi del Cilento, con panorami mozzafiato e testimonianze storiche.',
      distance: '4 km',
      duration: '2 ore e mezza',
      difficulty: 'Media',
      elevation: '693 m',
      distanceFromCasalVelino: '65-70 km',
      image: '/placeholder-trail.jpg',
      coordinates: {
        latitude: 40.0500,
        longitude: 15.4500
      },
      category: 'Storico',
      highlights: ['Eremi storici', 'Panorami montani', 'Testimonianze religiose'],
      tips: 'Percorso con dislivello importante. Portare scarpe da trekking.'
    },
    {
      id: '4',
      name: 'Sentieri Pagliaro e Valle Cupa – Celle di Bulgheria',
      description: 'Itinerario che si snoda tra valli e colline del Cilento, offrendo scorci naturalistici di grande bellezza.',
      distance: '11,2 km',
      duration: '3 ore e mezza',
      difficulty: 'Media',
      elevation: '860 m',
      distanceFromCasalVelino: '44 km',
      image: '/placeholder-trail.jpg',
      coordinates: {
        latitude: 40.1000,
        longitude: 15.3500
      },
      category: 'Naturalistico',
      highlights: ['Valli selvagge', 'Colline panoramiche', 'Natura incontaminata'],
      tips: 'Percorso di media difficoltà. Ideale per escursionisti esperti.'
    },
    {
      id: '5',
      name: 'Sentiero delle Scalette – Acquavena di Roccagloriosa',
      description: 'Un percorso impegnativo che attraversa paesaggi montani del Cilento, con passaggi tecnici e panorami spettacolari.',
      distance: '10 km',
      duration: '6 ore',
      difficulty: 'Difficile',
      elevation: '800 m',
      distanceFromCasalVelino: '47 km',
      image: '/placeholder-trail.jpg',
      coordinates: {
        latitude: 40.1200,
        longitude: 15.4000
      },
      category: 'Escursionistico',
      highlights: ['Passaggi tecnici', 'Panorami montani', 'Scalette naturali'],
      tips: 'Percorso molto impegnativo. Solo per escursionisti esperti.'
    },
    {
      id: '6',
      name: 'Sentiero della Tràgara o dei briganti – Centola',
      description: 'Un itinerario storico che ripercorre le antiche vie dei briganti, tra paesaggi selvaggi e testimonianze del passato.',
      distance: '11 km',
      duration: '6 ore',
      difficulty: 'Media-Difficile',
      elevation: '450 m',
      distanceFromCasalVelino: '41 km',
      image: '/placeholder-trail.jpg',
      coordinates: {
        latitude: 40.0800,
        longitude: 15.3000
      },
      category: 'Storico',
      highlights: ['Storia dei briganti', 'Paesaggi selvaggi', 'Testimonianze storiche'],
      tips: 'Percorso con valenza storica. Portare mappa e bussola.'
    },
    {
      id: '7',
      name: 'A guardia del Cilento – San Severino di Centola',
      description: 'Un percorso breve ma intenso che offre panorami mozzafiato sul Cilento e sul mare.',
      distance: '3 km',
      duration: '1 ora',
      difficulty: 'Facile',
      elevation: '650 m',
      distanceFromCasalVelino: '41 km',
      image: '/placeholder-trail.jpg',
      coordinates: {
        latitude: 40.0900,
        longitude: 15.3200
      },
      category: 'Panoramico',
      highlights: ['Panorami mozzafiato', 'Vista mare', 'Percorso breve'],
      tips: 'Percorso breve ma con dislivello. Ideale per tutti.'
    },
    {
      id: '8',
      name: 'La selvaggia bellezza della natura – Sant\'Angelo a Fasanella',
      description: 'Un percorso escursionistico che attraversa paesaggi incontaminati del Cilento interno.',
      distance: '1 km',
      duration: '1 ora e cinquanta',
      difficulty: 'Escursionistica',
      elevation: '100 m',
      distanceFromCasalVelino: '50 km',
      image: '/placeholder-trail.jpg',
      coordinates: {
        latitude: 40.2000,
        longitude: 15.2500
      },
      category: 'Naturalistico',
      highlights: ['Natura incontaminata', 'Cilento interno', 'Paesaggi selvaggi'],
      tips: 'Percorso per escursionisti. Portare attrezzatura adeguata.'
    }
  ];

  const difficulties = ['Facile', 'Media', 'Difficile', 'Escursionistica', 'Media-Difficile'];
  const categories = ['Escursionistico', 'Naturalistico', 'Storico', 'Panoramico', 'Culturale'];

  useEffect(() => {
    loadTrails();
  }, []);

  const loadTrails = async () => {
    try {
      setIsLoading(true);
      const trailsCol = collection(db, 'trails');
      const snapshot = await getDocs(trailsCol);
      const trailsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Trail[];
      
      // Se non ci sono sentieri nel database, usa quelli hardcoded
      if (trailsData.length === 0) {
        console.log('Nessun sentiero trovato in Firestore, usando dati hardcoded');
        setTrails(mobileTrails);
      } else {
        console.log(`Caricati ${trailsData.length} sentieri da Firestore`);
        setTrails(trailsData);
      }
    } catch (error) {
      console.error('Error loading trails:', error);
      // Fallback ai sentieri hardcoded
      setTrails(mobileTrails);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTrail = (trail: Trail) => {
    setSelectedTrail(trail);
    setFormData({
      name: trail.name,
      description: trail.description,
      distance: trail.distance,
      duration: trail.duration,
      difficulty: trail.difficulty,
      elevation: trail.elevation,
      distanceFromCasalVelino: trail.distanceFromCasalVelino,
      image: trail.image,
      coordinates: trail.coordinates || { latitude: 0, longitude: 0 },
      externalLink: trail.externalLink || '',
      category: trail.category,
      highlights: trail.highlights,
      tips: trail.tips,
      pois: trail.pois || []
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleNew = () => {
    setSelectedTrail(null);
    setFormData({
      name: '',
      description: '',
      distance: '',
      duration: '',
      difficulty: '',
      elevation: '',
      distanceFromCasalVelino: '',
      image: '',
      coordinates: {
        latitude: 0,
        longitude: 0
      },
      externalLink: '',
      category: '',
      highlights: [],
      tips: '',
      pois: []
    });
    setIsEditing(true);
  };


  const handleSave = async () => {
    try {
      if (selectedTrail) {
        await updateDoc(doc(db, 'trails', selectedTrail.id), formData);
        // Aggiorna la lista locale
        setTrails(prev => prev.map(t => t.id === selectedTrail.id ? { ...t, ...formData } : t));
        setSelectedTrail({ ...selectedTrail, ...formData });
      } else {
        const newTrail = await addDoc(collection(db, 'trails'), formData);
        const newTr = { id: newTrail.id, ...formData };
        setTrails(prev => [...prev, newTr]);
        setSelectedTrail(newTr);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving trail:', error);
    }
  };

  const handleCancel = () => {
    if (selectedTrail) {
      setFormData({
        name: selectedTrail.name,
        description: selectedTrail.description,
        distance: selectedTrail.distance,
        duration: selectedTrail.duration,
        difficulty: selectedTrail.difficulty,
        elevation: selectedTrail.elevation,
        distanceFromCasalVelino: selectedTrail.distanceFromCasalVelino,
        image: selectedTrail.image,
        coordinates: selectedTrail.coordinates || { latitude: 0, longitude: 0 },
        externalLink: selectedTrail.externalLink || '',
        category: selectedTrail.category,
        highlights: selectedTrail.highlights,
        tips: selectedTrail.tips
      });
    }
    setIsEditing(false);
  };

  const handleDelete = async (trailId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo sentiero?')) {
      try {
        await deleteDoc(doc(db, 'trails', trailId));
        setTrails(prev => prev.filter(t => t.id !== trailId));
        if (selectedTrail?.id === trailId) {
          setSelectedTrail(null);
        }
      } catch (error) {
        console.error('Error deleting trail:', error);
      }
    }
  };


  const addHighlight = (highlight: string) => {
    if (highlight.trim() && !formData.highlights.includes(highlight.trim())) {
      setFormData({
        ...formData,
        highlights: [...formData.highlights, highlight.trim()]
      });
    }
  };

  const removeHighlight = (highlight: string) => {
    setFormData({
      ...formData,
      highlights: formData.highlights.filter(h => h !== highlight)
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white p-6">
        <div className="container-max">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Mountain size={24} />
              <h1 className="text-2xl font-bold">Gestione Sentieri</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleNew}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Nuovo Sentiero</span>
              </button>
            </div>
          </div>
          <p className="text-white/80 mt-2">
            Gestisci i sentieri e i percorsi del Cilento
          </p>
        </div>
      </div>

      <div className="container-max p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista Sentieri */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-text-primary">Sentieri Disponibili</h2>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <div key={i} className="flex items-center space-x-3 p-3 animate-pulse">
                        <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-2">
                    {trails.map((trail) => (
                      <div
                        key={trail.id}
                        onClick={() => handleSelectTrail(trail)}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors duration-200 ${
                          selectedTrail?.id === trail.id
                            ? 'bg-primary/10 border border-primary/20'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={trail.image || '/placeholder-trail.jpg'} 
                            alt={trail.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-text-primary truncate">
                            {trail.name}
                          </h3>
                          <p className="text-sm text-text-secondary truncate">
                            {trail.distance} • {trail.duration}
                          </p>
                          <p className="text-xs text-text-secondary truncate">
                            {trail.difficulty}
                          </p>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectTrail(trail);
                              handleEdit();
                            }}
                            className="p-1 text-text-secondary hover:text-primary transition-colors duration-200"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(trail.id);
                            }}
                            className="p-1 text-text-secondary hover:text-error transition-colors duration-200"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form di Editing */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              {selectedTrail ? (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                        <img 
                          src={selectedTrail.image || '/placeholder-trail.jpg'} 
                          alt={selectedTrail.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-text-primary">
                          {selectedTrail.name}
                        </h2>
                        <p className="text-text-secondary">
                          {selectedTrail.distance} • {selectedTrail.duration}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleSave}
                            className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
                          >
                            <Save size={16} />
                            <span>Salva</span>
                          </button>
                          <button
                            onClick={handleCancel}
                            className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                          >
                            <X size={16} />
                            <span>Annulla</span>
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleEdit}
                          className="flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary/90 transition-colors duration-200"
                        >
                          <Edit size={16} />
                          <span>Modifica</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Informazioni Base */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Nome del Sentiero *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Descrizione *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        disabled={!isEditing}
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    {/* Dettagli Tecnici */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Distanza *
                        </label>
                        <input
                          type="text"
                          value={formData.distance}
                          onChange={(e) => setFormData({ ...formData, distance: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                          placeholder="es. 16,98 km"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Durata *
                        </label>
                        <input
                          type="text"
                          value={formData.duration}
                          onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                          placeholder="es. 7 ore"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Difficoltà *
                        </label>
                        <select
                          value={formData.difficulty}
                          onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        >
                          <option value="">Seleziona difficoltà</option>
                          {difficulties.map((diff) => (
                            <option key={diff} value={diff}>
                              {diff}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Dislivello *
                        </label>
                        <input
                          type="text"
                          value={formData.elevation}
                          onChange={(e) => setFormData({ ...formData, elevation: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                          placeholder="es. 470 m"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Distanza da Casal Velino
                        </label>
                        <input
                          type="text"
                          value={formData.distanceFromCasalVelino}
                          onChange={(e) => setFormData({ ...formData, distanceFromCasalVelino: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                          placeholder="es. 0 km"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Categoria
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        >
                          <option value="">Seleziona categoria</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Immagine */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Immagine
                      </label>
                      <div className="flex items-center space-x-4">
                        <div className="w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={formData.image || '/placeholder-trail.jpg'} 
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="url"
                            value={formData.image}
                            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="URL dell'immagine"
                          />
                          {isEditing && (
                            <button className="mt-2 flex items-center space-x-2 text-primary hover:text-secondary transition-colors duration-200">
                              <Upload size={16} />
                              <span className="text-sm">Carica immagine</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Highlights */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Punti di Interesse
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.highlights.map((highlight, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-secondary/20 text-secondary text-sm rounded-full flex items-center space-x-2"
                          >
                            <span>{highlight}</span>
                            {isEditing && (
                              <button
                                onClick={() => removeHighlight(highlight)}
                                className="text-secondary hover:text-error"
                              >
                                ✕
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {isEditing && (
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            placeholder="Aggiungi punto di interesse"
                            className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addHighlight(e.currentTarget.value);
                                e.currentTarget.value = '';
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                              addHighlight(input.value);
                              input.value = '';
                            }}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                          >
                            Aggiungi
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Consigli */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Consigli per il Percorso
                      </label>
                      <textarea
                        value={formData.tips}
                        onChange={(e) => setFormData({ ...formData, tips: e.target.value })}
                        disabled={!isEditing}
                        rows={3}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="Consigli utili per chi percorre il sentiero"
                      />
                    </div>

                    {/* Link Esterno */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Link Esterno (opzionale)
                      </label>
                      <input
                        type="url"
                        value={formData.externalLink}
                        onChange={(e) => setFormData({ ...formData, externalLink: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="https://..."
                      />
                    </div>

                    {/* Coordinate con Google Maps */}
                    <MapCoordinatePicker
                      latitude={formData.coordinates?.latitude || 0}
                      longitude={formData.coordinates?.longitude || 0}
                      onChange={(lat, lng) => setFormData({
                        ...formData,
                        coordinates: { latitude: lat, longitude: lng }
                      })}
                      disabled={!isEditing}
                    />

                    {/* Gestione POI */}
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">
                          <Mountain size={20} className="inline mr-2" />
                          Punti di Interesse (POI)
                        </h3>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingPOI(true);
                            setSelectedPOI(null);
                            setPoiFormData({
                              id: Date.now().toString(),
                              name: '',
                              description: '',
                              coordinates: { latitude: formData.coordinates?.latitude || 40.1689, longitude: formData.coordinates?.longitude || 15.1428 },
                              type: 'punto_interesse',
                              connectedPOIs: []
                            });
                          }}
                          disabled={!isEditing}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                        >
                          <Plus size={16} />
                          <span>Aggiungi POI</span>
                        </button>
                      </div>

                      {/* Lista POI */}
                      {formData.pois && formData.pois.length > 0 ? (
                        <div className="grid grid-cols-1 gap-3 mb-4">
                          {formData.pois.map((poi) => (
                            <div
                              key={poi.id}
                              className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between hover:border-primary transition-colors"
                            >
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-800">{poi.name}</h4>
                                <p className="text-sm text-gray-600 mt-1">{poi.description}</p>
                                <p className="text-xs text-gray-500 mt-2">
                                  📍 {poi.coordinates.latitude.toFixed(6)}, {poi.coordinates.longitude.toFixed(6)}
                                </p>
                                {poi.connectedPOIs && poi.connectedPOIs.length > 0 && (
                                  <p className="text-xs text-primary mt-1">
                                    🔗 Collegato a {poi.connectedPOIs.length} POI
                                  </p>
                                )}
                              </div>
                              {isEditing && (
                                <div className="flex space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedPOI(poi);
                                      setPoiFormData(poi);
                                      setIsEditingPOI(true);
                                    }}
                                    className="text-primary hover:text-primary/80"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        pois: formData.pois?.filter(p => p.id !== poi.id)
                                      });
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                          <Mountain size={32} className="mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-500 text-sm">
                            Nessun punto di interesse. Clicca su "Aggiungi POI" per iniziare.
                          </p>
                        </div>
                      )}

                      {/* Form POI Modal */}
                      {isEditingPOI && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-xl font-semibold text-gray-800">
                                {selectedPOI ? 'Modifica POI' : 'Nuovo POI'}
                              </h3>
                              <button
                                onClick={() => {
                                  setIsEditingPOI(false);
                                  setSelectedPOI(null);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X size={24} />
                              </button>
                            </div>

                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Nome POI
                                </label>
                                <input
                                  type="text"
                                  value={poiFormData.name}
                                  onChange={(e) => setPoiFormData({ ...poiFormData, name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                  placeholder="es. Belvedere Monte Stella"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Descrizione
                                </label>
                                <textarea
                                  value={poiFormData.description}
                                  onChange={(e) => setPoiFormData({ ...poiFormData, description: e.target.value })}
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                  placeholder="Descrizione del punto di interesse..."
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Tipo
                                </label>
                                <select
                                  value={poiFormData.type}
                                  onChange={(e) => setPoiFormData({ ...poiFormData, type: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                >
                                  <option value="punto_interesse">Punto di Interesse</option>
                                  <option value="belvedere">Belvedere</option>
                                  <option value="fontana">Fontana</option>
                                  <option value="rifugio">Rifugio</option>
                                  <option value="ristoro">Punto Ristoro</option>
                                </select>
                              </div>

                              <MapCoordinatePicker
                                latitude={poiFormData.coordinates.latitude}
                                longitude={poiFormData.coordinates.longitude}
                                onChange={(lat, lng) => setPoiFormData({
                                  ...poiFormData,
                                  coordinates: { latitude: lat, longitude: lng }
                                })}
                                disabled={false}
                              />

                              {/* Collegamenti ad altri POI */}
                              {formData.pois && formData.pois.length > 0 && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Collega ad altri POI
                                  </label>
                                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                                    {formData.pois
                                      .filter(p => p.id !== poiFormData.id)
                                      .map((poi) => (
                                        <label key={poi.id} className="flex items-center space-x-2">
                                          <input
                                            type="checkbox"
                                            checked={poiFormData.connectedPOIs?.includes(poi.id) || false}
                                            onChange={(e) => {
                                              const currentConnected = poiFormData.connectedPOIs || [];
                                              if (e.target.checked) {
                                                setPoiFormData({
                                                  ...poiFormData,
                                                  connectedPOIs: [...currentConnected, poi.id]
                                                });
                                              } else {
                                                setPoiFormData({
                                                  ...poiFormData,
                                                  connectedPOIs: currentConnected.filter(id => id !== poi.id)
                                                });
                                              }
                                            }}
                                            className="rounded border-gray-300 text-primary focus:ring-primary"
                                          />
                                          <span className="text-sm text-gray-700">{poi.name}</span>
                                        </label>
                                      ))}
                                  </div>
                                </div>
                              )}

                              <div className="flex space-x-3 pt-4">
                                <button
                                  onClick={() => {
                                    if (selectedPOI) {
                                      setFormData({
                                        ...formData,
                                        pois: formData.pois?.map(p => p.id === poiFormData.id ? poiFormData : p)
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        pois: [...(formData.pois || []), poiFormData]
                                      });
                                    }
                                    setIsEditingPOI(false);
                                    setSelectedPOI(null);
                                  }}
                                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                                >
                                  Salva POI
                                </button>
                                <button
                                  onClick={() => {
                                    setIsEditingPOI(false);
                                    setSelectedPOI(null);
                                  }}
                                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                  Annulla
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Mountain size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    Seleziona un Sentiero
                  </h3>
                  <p className="text-text-secondary">
                    Scegli un sentiero dalla lista per modificarlo o creane uno nuovo
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrailsNew;
